import { findIndex } from "lodash";
import { getI18n } from "react-i18next";

import { FieldRequestTypeEnum } from "@krea/shared/types/fieldRequest";

import { FieldRequestFieldEnum, IFieldRequest } from "../types/fieldRequest";

const FIELD_REQUEST_WITH_TOAST_NAMES: Record<string, FieldRequestFieldEnum> = {
  REASON: FieldRequestFieldEnum.reason_explanation,
  REFINANCING_AMOUNTS: FieldRequestFieldEnum.refinancing_amounts,
  LEASING: FieldRequestFieldEnum.leasing_additional_data,
  PROPERTY_ADDITIONAL_DATA: FieldRequestFieldEnum.property_additional_data,
  GUARANTORS: FieldRequestFieldEnum.guarantors,
};

export const FIELD_REQUEST_FIELDS = FIELD_REQUEST_WITH_TOAST_NAMES;

export const getDefaultToastProperties = (steps: IFieldRequest[]) => {
  const { t } = getI18n();
  const numberOfQuestions = steps.length || 0;

  return {
    [FIELD_REQUEST_WITH_TOAST_NAMES.REASON]: {
      warning: true,
      title: t(
        `triageFields.toastTexts.${FIELD_REQUEST_WITH_TOAST_NAMES.REASON}.title`,
      ),
      subtitle: t(
        `triageFields.toastTexts.${FIELD_REQUEST_WITH_TOAST_NAMES.REASON}.subtitle`,
        { numberOfQuestions },
      ),
    },
    [FIELD_REQUEST_WITH_TOAST_NAMES.REFINANCING_AMOUNTS]: {
      skippable: true,
      title: t(
        `triageFields.toastTexts.${FIELD_REQUEST_WITH_TOAST_NAMES.REFINANCING_AMOUNTS}.title`,
      ),
      subtitle: t(
        `triageFields.toastTexts.${FIELD_REQUEST_WITH_TOAST_NAMES.REFINANCING_AMOUNTS}.subtitle`,
      ),
    },
    [FIELD_REQUEST_WITH_TOAST_NAMES.GUARANTORS]: {
      closable: true,
      warning: true,
      title: t(
        `triageFields.toastTexts.${FIELD_REQUEST_WITH_TOAST_NAMES.GUARANTORS}.title`,
      ),
      subtitle: t(
        `triageFields.toastTexts.${FIELD_REQUEST_WITH_TOAST_NAMES.GUARANTORS}.subtitle`,
      ),
    },
    [FIELD_REQUEST_WITH_TOAST_NAMES.LEASING]: {
      title: t(
        `triageFields.toastTexts.${FIELD_REQUEST_WITH_TOAST_NAMES.LEASING}.title`,
      ),
      subtitle: t(
        `triageFields.toastTexts.${FIELD_REQUEST_WITH_TOAST_NAMES.LEASING}.subtitle`,
      ),
    },
    [FIELD_REQUEST_WITH_TOAST_NAMES.PROPERTY_ADDITIONAL_DATA]: {
      title: t(
        `triageFields.toastTexts.${FIELD_REQUEST_WITH_TOAST_NAMES.PROPERTY_ADDITIONAL_DATA}.title`,
      ),
      subtitle: t(
        `triageFields.toastTexts.${FIELD_REQUEST_WITH_TOAST_NAMES.PROPERTY_ADDITIONAL_DATA}.subtitle`,
      ),
    },
  };
};

export const getDefaultToastStates = () => ({
  [FIELD_REQUEST_WITH_TOAST_NAMES.REASON]: { isOpen: true },
  [FIELD_REQUEST_WITH_TOAST_NAMES.REFINANCING_AMOUNTS]: { isOpen: true },
  [FIELD_REQUEST_WITH_TOAST_NAMES.GUARANTORS]: { isOpen: true },
  [FIELD_REQUEST_WITH_TOAST_NAMES.LEASING]: { isOpen: true },
  [FIELD_REQUEST_WITH_TOAST_NAMES.PROPERTY_ADDITIONAL_DATA]: { isOpen: true },
});

export const getFilteredFieldRequests = (
  fieldsToFilter: string[] = [],
  fieldRequests: IFieldRequest[] = [],
) => {
  if (fieldsToFilter.length) {
    const result: IFieldRequest[] = [];

    fieldsToFilter.forEach((field) =>
      fieldRequests.forEach((item) => {
        if ((item.field as string) === field) {
          result.push(item);
        }
      }),
    );

    return result;
  } else {
    return fieldRequests;
  }
};

export const getNextRequest = (
  triageFields: string[],
  fieldRequests: IFieldRequest[],
  field: FieldRequestFieldEnum,
) => {
  const triageFieldsResult = triageFields.length
    ? getFilteredFieldRequests(triageFields, fieldRequests)
    : fieldRequests;

  const stepIndex = findIndex(
    triageFieldsResult,
    (request) => request.field === field,
  );
  const nextStep =
    stepIndex !== triageFieldsResult.length - 1 ? stepIndex + 1 : null;

  return nextStep ? triageFieldsResult[nextStep] : undefined;
};

export const getPreviousRequest = (
  triageFields: string[],
  fieldRequests: IFieldRequest[],
  field: FieldRequestFieldEnum,
) => {
  const triageFieldsResult = triageFields.length
    ? getFilteredFieldRequests(triageFields, fieldRequests)
    : fieldRequests;

  const stepIndex = findIndex(
    triageFieldsResult,
    (request) => request.field === field,
  );
  const previousStep = stepIndex !== 0 ? stepIndex - 1 : null;

  return previousStep != null ? triageFieldsResult[previousStep] : undefined;
};

export const hasFieldRequestsToUpdate = (
  fieldRequests: IFieldRequest[],
  shouldConsiderLenderComment: boolean,
) => {
  // return true if there is at least one field request to update
  // =======
  // If changes are made here, also look on the backend, to check that the logic for LenderResponse.unsubmittedFieldRequests is the same
  // (not taking the lenderComment into account, which is fine).
  return (
    fieldRequests.filter(
      ({ lenderComment, isCollectedByLender, isSubmitted, field, type }) => {
        if (
          type === FieldRequestTypeEnum.COLLATERAL &&
          field !== FieldRequestFieldEnum.guarantors
        ) {
          return false; // don't consider collateral field requests (with the exception of guarantors)
        }
        // If the field request is collected by lender, we don't want to consider it
        if (isCollectedByLender) return false;

        if (isSubmitted) {
          if (shouldConsiderLenderComment) {
            // only consider a submitted FR if there is a lenderComment and we want to consider it
            if (lenderComment) {
              return true;
            } else {
              return false;
            }
          } else {
            // submitted FRs will not be considered if we don't want to consider lenderComment
            return false;
          }
        } else {
          // unsubmitted FRs will be considered if they are not collected by lender
          return true;
        }
      },
    ).length > 0
  );
};
